import { Component, inject, Input, signal, ViewChild } from '@angular/core';
import {
    FrameworkColumn,
    ITableCellValueRenderer,
    SafeAny,
} from '@pf/shared-common';
import { joinStrings } from '@pf/shared-utility';
import { UserFacade } from '@pf/platform-core/domain';
import { FormControl } from '@angular/forms';
import { IUserFragment } from '@pf/shared/util-platform';
import { SelectComponent } from '@pf/shared-ui';
import { Observable, tap } from 'rxjs';

export interface IUserAssignedToRendererComponentProps {
    onAssignUser: (
        record: SafeAny,
        userFragment: IUserFragment
    ) => Observable<SafeAny> | void;
}

@Component({
    selector: 'platform-user-assigned-to-renderer',
    template: `
        <span>{{ assignedUser() || 'No assignment' }}</span>
        @if (assignedUser() === '') {
            <pf-button
                nz-popover
                nzPopoverTitle="Assign User"
                [nzPopoverContent]="assignUserContent"
                [nzPopoverTrigger]="null"
                [nzPopoverVisible]="assignUserPopoverVisible()"
                [nzPopoverOverlayStyle]="{ 'max-width': 'none' }"
                buttonType="action"
                (click)="onAssignClick()"
                >Assign</pf-button
            >

            <ng-template #assignUserContent>
                <div
                    style="display: flex; flex-direction: column; gap: 0.5rem; width: 280px;">
                    <pf-select
                        #assignUserSelect
                        pfStyle="stacked"
                        style="--pf-form-control-margin-bottom: 0;"
                        label="Select user to assign"
                        [control]="assignUserControl"
                        [searchFacade]="userFacade"></pf-select>
                    <div style="display: flex; justify-content: space-between;">
                        <pf-button buttonType="cancel" (click)="closePopover()"
                            >Cancel</pf-button
                        >
                        <pf-button
                            buttonType="submit"
                            (click)="assignUser()"
                            [disabled]="!assignUserControl.value"
                            [loading]="assigning()"
                            >Assign</pf-button
                        >
                    </div>
                </div>
            </ng-template>
        }
    `,
    styles: [
        `
            :host {
                display: flex;
                flex-direction: column;

                .assign-user-container {
                    display: flex;
                    flex-direction: column;
                }

                .assign-user-actions {
                    display: flex;
                    justify-content: space-between;
                    margin-top: 0.5rem;
                    width: 100%;
                }
            }
        `,
    ],
    standalone: false,
})
export class UserAssignedToRendererComponent
    implements ITableCellValueRenderer, IUserAssignedToRendererComponentProps
{
    @ViewChild('assignUserSelect') assignUserSelect!: SelectComponent;
    readonly userFacade = inject(UserFacade);

    assignUserControl = new FormControl<string | null>(null);
    assignedUser = signal<string>('');
    assignUserPopoverVisible = signal<boolean>(false);
    assigning = signal<boolean>(false);
    @Input() set value(user: string | IUserFragment | undefined) {
        if (!user) {
            this.assignedUser.set('');
        } else if (typeof user === 'string') {
            this.assignedUser.set(user);
        } else {
            this.assignedUser.set(
                joinStrings(' ', user.firstName, user.lastName)
            );
        }
    }
    @Input() column!: FrameworkColumn;
    @Input() row!: SafeAny;
    @Input()
    onAssignUser!: IUserAssignedToRendererComponentProps['onAssignUser'];

    protected onAssignClick() {
        this.assignUserPopoverVisible.set(true);
        setTimeout(() => {
            this.assignUserSelect.focus();
            this.assignUserSelect.open();
        }, 100);
    }

    protected closePopover() {
        this.assignUserPopoverVisible.set(false);
        this.assignUserControl.reset();
    }

    protected assignUser() {
        if (!this.assignUserControl.value) {
            return;
        }
        const user = this.userFacade.getById(this.assignUserControl.value);

        if (!user) {
            return;
        }

        const userFragment: IUserFragment = {
            id: user.id,
            firstName: user.firstName as string,
            lastName: user.lastName as string,
            email: user.email as string,
        };

        const result = this.onAssignUser(this.row, userFragment);

        if (result instanceof Observable) {
            this.assigning.set(true);
            result
                .pipe(
                    tap(() => {
                        this.assigning.set(false);
                        this.assignUserPopoverVisible.set(false);
                        this.assignedUser.set(
                            joinStrings(
                                ' ',
                                userFragment.firstName,
                                userFragment.lastName
                            )
                        );
                        this.assignUserControl.reset();
                    })
                )
                .subscribe();
        } else {
            this.assignUserPopoverVisible.set(false);
            this.assignedUser.set(
                joinStrings(' ', userFragment.firstName, userFragment.lastName)
            );
            this.assignUserControl.reset();
        }
    }
}
